import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { saveGratitudeEntry } from '../utils/localStorage'
import { useTheme } from './ThemeProvider'

const GratitudeJournal = () => {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  const [entry, setEntry] = useState('')
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false)
  const [savedEntries, setSavedEntries] = useState<number>(0)

  const prompts = [
    "What's something that felt okay today?",
    "What made you feel a little safer or calmer?",
    "What's one small thing that went smoothly?",
    "Who or what brought you a moment of peace?",
    "What's something you're grateful for, even if it's tiny?",
    "What helped you get through today?",
    "What felt gentle or kind today?"
  ]

  const [currentPrompt, setCurrentPrompt] = useState(prompts[Math.floor(Math.random() * prompts.length)])

  const handleSave = () => {
    if (entry.trim()) {
      // Save to local storage
      saveGratitudeEntry(entry, currentPrompt)
      
      // Show confirmation briefly
      setShowSaveConfirmation(true)
      setSavedEntries(prev => prev + 1)
      
      // Clear the entry and get a new prompt
      setEntry('')
      setCurrentPrompt(prompts[Math.floor(Math.random() * prompts.length)])
      
      // Hide confirmation after 2 seconds
      setTimeout(() => {
        setShowSaveConfirmation(false)
      }, 2000)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey && entry.trim()) {
      handleSave()
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-cream-25 dark:bg-sage-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 sm:p-6 lg:p-8 bg-cream-50/80 dark:bg-sage-800/80 border-b border-cream-100/50 dark:border-sage-700/50">
        <button
          onClick={() => navigate('/tools')}
          className="p-3 sm:p-4 rounded-2xl hover:bg-cream-100 dark:hover:bg-sage-700 transition-colors focus:outline-none focus:ring-4 focus:ring-cream-100 dark:focus:ring-sage-700"
        >
          <svg className="w-5 h-5 text-cream-500 dark:text-cream-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="text-center">
          <h1 className="text-base sm:text-lg font-light text-cream-600 dark:text-cream-300">Gratitude Journal</h1>
          {savedEntries > 0 && (
            <p className="text-cream-500 dark:text-cream-400 text-xs font-light mt-1">
              {savedEntries} {savedEntries === 1 ? 'entry' : 'entries'} saved today
            </p>
          )}
        </div>
        <button
          onClick={() => navigate('/journal-entries')}
          className="p-3 sm:p-4 rounded-2xl hover:bg-cream-100 dark:hover:bg-sage-700 transition-colors focus:outline-none focus:ring-4 focus:ring-cream-100 dark:focus:ring-sage-700"
        >
          <svg className="w-5 h-5 text-cream-500 dark:text-cream-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </button>
      </div>

      {/* Journal */}
      <div className="flex-1 flex flex-col px-4 sm:px-6 lg:px-8 py-8 sm:py-12 overflow-hidden">
        <div className="text-center mb-8 sm:mb-12">
          <div className="text-xl sm:text-2xl mb-4">📓</div>
          <p className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base leading-relaxed max-w-sm mx-auto">
            Keep writing as long as you'd like. Each entry is saved automatically.
          </p>
        </div>

        {/* Save Confirmation */}
        {showSaveConfirmation && (
          <div className="max-w-md mx-auto w-full mb-6">
            <div className="bg-sage-50/80 dark:bg-sage-800/80 p-4 rounded-2xl border border-sage-100/50 dark:border-sage-700/50 text-center">
              <div className="flex items-center justify-center space-x-2">
                <svg className="w-4 h-4 text-sage-500 dark:text-sage-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-sage-600 dark:text-sage-300 font-light text-sm">
                  Entry saved ✨
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Writing Space */}
        <div className="max-w-md mx-auto w-full">
          <div className="bg-cream-50 dark:bg-sage-800 p-6 sm:p-8 rounded-3xl border border-cream-100/50 dark:border-sage-700/50 mb-6 sm:mb-8 overflow-hidden">
            <p className="text-cream-600 dark:text-cream-300 font-light text-sm sm:text-base mb-6 text-center break-words">
              {currentPrompt}
            </p>
            
            <textarea
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Start writing here... there's no wrong way to do this."
              className="w-full p-4 rounded-2xl border border-cream-200/50 dark:border-sage-700/50 focus:border-cream-300 dark:focus:border-sage-600 focus:ring-2 focus:ring-cream-100 dark:focus:ring-sage-600 focus:outline-none font-light resize-none text-sm bg-white/50 dark:bg-sage-900/50 dark:text-cream-300 min-h-32 break-words overflow-wrap-anywhere"
              rows={4}
              autoFocus
              style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            />
            
            {/* Character count helper */}
            <div className="mt-2 text-right">
              <span className="text-cream-400 dark:text-cream-500 text-xs font-light">
                {entry.length} characters
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="text-center space-y-4">
            {/* Save Button - Always visible when there's content */}
            <div className="min-h-[60px] flex items-center justify-center">
              {entry.trim().length > 0 ? (
                <button
                  onClick={handleSave}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-cream-100/80 hover:bg-cream-200/60 
                             dark:bg-cream-900/30 dark:hover:bg-cream-800/40
                             text-cream-600 dark:text-cream-300 rounded-2xl text-sm sm:text-base font-light 
                             border border-cream-200/50 dark:border-cream-700/40 
                             hover:border-cream-300/60 dark:hover:border-cream-600/50
                             transition-colors duration-300 focus:outline-none focus:ring-4 
                             focus:ring-cream-100 dark:focus:ring-cream-700/30
                             transform hover:scale-105"
                >
                  Save & continue writing
                </button>
              ) : (
                <p className="text-cream-400 dark:text-cream-500 text-sm font-light">
                  Start writing to save your thoughts...
                </p>
              )}
            </div>
            
            {/* Alternative actions */}
            <div className="space-y-2">
              <button
                onClick={() => navigate('/journal-entries')}
                className="block mx-auto text-cream-500 dark:text-cream-400 text-sm underline hover:text-cream-600 dark:hover:text-cream-300 transition-colors"
              >
                View previous entries ({savedEntries > 0 ? `${savedEntries} new` : 'all'})
              </button>
              
              <button
                onClick={() => navigate('/tools')}
                className="block mx-auto text-cream-400 dark:text-cream-500 text-sm hover:text-cream-500 dark:hover:text-cream-400 transition-colors"
              >
                Return to tools
              </button>
            </div>
          </div>
        </div>

        {/* Gentle Footer */}
        <div className="mt-12 sm:mt-16 text-center">
          <p className="text-cream-400 dark:text-cream-500 text-xs sm:text-sm font-light leading-relaxed max-w-xs mx-auto mb-2">
            Write as many entries as you'd like. Each one is saved as you go.
          </p>
          <p className="text-cream-400 dark:text-cream-500 text-xs font-light">
            Tip: Press Ctrl+Enter to save quickly
          </p>
        </div>
      </div>
    </div>
  )
}

export default GratitudeJournal